import {ChangeDetectorRef, Component, Input, NgZone, TemplateRef, ViewChild} from '@angular/core';
import {Phase, Project, ProjectStatus} from "../interfaces/project";
import {Globals} from "../shared/Globals";
import moment from "moment";
import {ClientChange, ProjectService} from "../shared/project.service";
import {TranslateService} from "@ngx-translate/core";
import {NbDialogService, NbLayoutScrollService} from "@nebular/theme";
import {ActivatedRoute, Router} from "@angular/router";
import {SubscribedComponent} from "../subscribed.component";
import {DirectoryService} from "../shared/directory.service";
import {FormBuilder, FormGroup} from "@angular/forms";
import {BSValidators} from "../shared/bsvalidators";
import {UsersService} from "../shared/users.service";
import {AuthService} from "../shared/auth.service";
import {ProjectRole} from "../interfaces/projectRole";
import {Client} from "../interfaces/client";
import {PopupDialogComponent} from "../dialogs/popup-dialog/popup-dialog.component";
import {ClientComponent} from "../clients/client.component";
import {ClientService} from "../shared/client.service";
import {UserProjectAssignerComponent} from "../controls/user-project-assigner/user-project-assigner.component";
import {NotifierService} from "../shared/notifier.service";
import {TableType} from "../interfaces/paged";
import {TimelineType} from "../charts/timeline-chart/timeline-chart.component";
import {SettingsService} from "../shared/settings.service";
import {MemberAssignment} from "../interfaces/memberAssignment";
import {HoursOverviewDialogComponent} from "./hours-overview-dialog/hours-overview-dialog.component";
import {UserStatus} from "../interfaces/user";

@Component({
    selector: 'bst-project',
    templateUrl: './project.component.html',
    styleUrls: ['./projects.component.scss'],
    standalone: false
})
export class ProjectComponent extends SubscribedComponent{

  @Input('id') public id: number = null;
  @Input('couldedit') public couldEdit: boolean = true;
  @Input('couldarchive') public couldArchive: boolean = true;
  @Input() public showTimeline:boolean = true;

  @ViewChild('assignerComponent', {static: false}) assignerComponent: UserProjectAssignerComponent;
  // @ViewChild('userResultHeaderTemplate', {static: true}) userResultHeaderTemplate: TemplateRef<any>;
  // @ViewChild('userResultBodyTemplate', {static: true}) userResultBodyTemplate: TemplateRef<any>;
  // @ViewChild('userSelectionHeaderTemplate', {static: true}) userSelectionHeaderTemplate: TemplateRef<any>;
  // @ViewChild('userSelectionBodyTemplate', {static: true}) userSelectionBodyTemplate: TemplateRef<any>;

  public project: Project = null;
  public embedded: boolean = false;
  public Globals = Globals;
  public TableType = TableType;
  public ProjectStatus = ProjectStatus;
  public projectroles: ProjectRole[];
  public editing: boolean = false;
  public addnew = ($event: any) => {
    return new Promise((resolve, reject) => {
      const client = this.clientService.emptyClient;
      const sub = this.dialogService.open<PopupDialogComponent<ClientComponent>>(PopupDialogComponent, {
        closeOnBackdropClick: false,
        context:{
          comp: ClientComponent,
          componentContext: {
            client: client,
            editing: true,
            couldArchive: false
          }
        }
      }).onClose.subscribe((o: ClientComponent) => {
        let item: Client = null;
        if(o.client && o.client.id) {
          item = o.client;
        }
        if(sub){
          sub.unsubscribe();
        }
        return resolve(item);
      });
    });
  };

  // Assignments in "edit mode"
  // public assignments: MemberAssignment[] = null;
  // Assignments that may be added while searching
  // public newassignments: MemberAssignment[] = [];

  public form: FormGroup;
  public assignmentForm: FormGroup;
  height: string = null;

  constructor(private projectService: ProjectService,
              private translate: TranslateService,
              private clientService: ClientService,
              private router: Router,
              private route: ActivatedRoute,
              private detector: ChangeDetectorRef,
              private directoryService: DirectoryService,
              private userService: UsersService,
              private fb: FormBuilder,
              private zone: NgZone,
              private notifier: NotifierService,
              public scroll: NbLayoutScrollService,
              public auth: AuthService,
              private settings: SettingsService,
              private dialogService: NbDialogService) {
    super();
  }

  ngOnInit(): void {

    const controls = {
      name: [null, [BSValidators.required, BSValidators.maxLength(100)]],
      description: [null, [BSValidators.maxLength(30000)]],
      color: [null, [BSValidators.required]],
      for_Client_id: [null, [BSValidators.required]],
      dates: [null, [BSValidators.required, BSValidators.dateRange()]],
      status: [null, [BSValidators.required]],
      // duedate: [null, []],
      industry: [null, [BSValidators.minElements(1)]],
      skills: [null, []],
      developments: [null, []],
      certifications: [null, []],
      // assignments: [{value: null, disabled: !this.editing}, []]
      billable: [null, []],
    };

    this.form = this.fb.group(controls);
    this.assignmentForm = this.fb.group({
      assignments: [{value: null, disabled: !this.canEdit}, []]
    });

    // Capture popup-vs-routed mode synchronously, before async route work runs: every popup
    // embedder sets `id` (see ClientComponent.embedded for the analogous field + the
    // PopupDialogComponent comment on when the instance is assigned).
    this.embedded = this.id != null;

    if(this.id){
      this.mapProject(this.id);
    }
    else if(this.project){
      this.resetForm();
      this.resetAssignments();
    }
    else {
      this.subs.push(this.route.paramMap.subscribe(async (paramMap) => {
        const id = paramMap.get('id');
        if (id) {
          await this.mapProject(parseInt(id));
        }

        const edit = this.route.snapshot.queryParamMap.get('edit');
        if (this.project && this.project.status !== ProjectStatus.ARCHIVED && (edit == "true" || edit == "1")) {
          this.editProject();
        }

        const assignId = +(this.route.snapshot.queryParamMap.get('assign') ?? 0);
        if (assignId && this.project && this.project.status !== ProjectStatus.ARCHIVED) {
          // Defer one tick so the assigner ViewChild is live and phases are written.
          setTimeout(() => { this.assignerComponent?.preselectUser(assignId); }, 0);
        }

        // A deep link from an invoice issue (?field=invoices|members): the sections themselves
        // have no editable field, so scroll to them instead of focusing. vat_option and
        // client_reference go to bst-invoicing-project via the focusField input instead.
        if (this.project && ['invoices', 'members'].includes(this.focusField)) {
          setTimeout(() => { document.getElementById('project-' + this.focusField)?.scrollIntoView({block: 'start'}); });
        }

      }));
    }

    // live changes
    this.subs.push(this.assignmentForm.valueChanges.subscribe(async (val) => {
      await this.saveAssignments();
    }));
  }

  private resetAssignments(){
    const members = (this.project.phases || []).map(o => {
      o.members.forEach(o => {
        o.start_on = Globals.fromDateString(o.start_on);
        o.end_on = Globals.fromDateString(o.end_on);
      })
      return o;
    });

    this.assignmentForm.setValue({
      assignments: members
    }, {
      emitEvent: false
    });

    if(this.canEdit && this.project.status !== ProjectStatus.ARCHIVED){
      this.assignmentForm.enable({onlySelf: true, emitEvent: false});
    }
    else{
      this.assignmentForm.disable({onlySelf: true, emitEvent: false});
    }
  }

  private resetForm(){
    const values = {
      name: this.project.name,
      description: this.project.description,
      color: this.project.color,
      for_Client_id: this.project.for_client,
      dates: {
        start: Globals.fromDateStringToMoment(this.project.start_on),
        end: Globals.fromDateStringToMoment(this.project.end_on)
      },
      status: this.project.status,
      // duedate: Globals.fromDateString(this.project.due_on),
      industry: this.project.industries,
      skills: this.project.skills,
      developments: this.project.developments,
      certifications: this.project.certifications,
      // assignments: members
      billable: this.project.billable ?? null,
    };

    this.form.setValue(values, {
      emitEvent: false
    });

    this.form.markAsPristine();
    this.form.markAsUntouched();
  }

  get isDirty(): boolean {
    return this.editing && this.form.dirty && this.form.touched;
  }

  private async mapProject(id: number){
    this.loading.next(true);

    this.projectroles = await this.directoryService.getProjectRoles().catch(() => []);
    // Toasted by errorify; a missing project falls through to the redirect below.
    this.project = await this.projectService.getProject(id).catch(() => null);
    if(!this.project){
      await this.router.navigate(['projects']);
    }

    this.resetForm();
    this.resetAssignments();

    this.loading.next(false);
  }

  public unarchiveProject(project: Project) {
    Globals.loading = true;
    this.projectService.changeProjectStatus(project.id, ProjectStatus.ACTIVE).then((project) => {
      this.project = project;
      Globals.loading = false;
    });
  }

  public async archiveProject(project: Project){
    if(await this.notifier.confirm('archivecheck', project.name)){
      await this.projectService.changeProjectStatus(project.id, ProjectStatus.ARCHIVED);
      await this.router.navigate(['projects']);
    }
  }

  public async deleteProject(project: Project){
    if(await this.notifier.confirm('deletecheck', project.name, 'deleteforever', 'danger')){
      await this.projectService.deleteProject(project.id, this.translate.currentLang);
      await this.router.navigate(['projects'], {
        queryParams:{
          status: ProjectStatus.ARCHIVED
        }
      });
    }
  }

  public editProject() {
    this.editing = true;
  }

  public closeProject(){
    this.editing = false;
  }

  public cancelProject(){
    this.resetForm();
    return this.closeProject();
  }

  private get assignments(){
    const results = {assignments: [], phases: []};
    const phases = this.assignmentForm.get('assignments').value;
    if(phases){
      results.phases = phases.map((p: Phase) => {
        results.assignments = results.assignments.concat(p.members.map((o: MemberAssignment) => {
          return {
            id: o.id,
            User_id: o.user?.id,
            Phase_id: o.Phase_id || p.id,
            start_on: Globals.toISODate(o.start_on),
            end_on: Globals.toISODate(o.end_on),
            dedication: o.dedication,
            cost: o.cost,
            offered: o.offered,
            status: o.status,
            description: o.description,
            ProjectRole_id: o.project_role ? o.project_role.id : (o.projectRole_id ? o.projectRole_id : null)
          };
        }));

        return {
          id: p.id,
          Project_id: this.project.id,
          name: p.name,
          description: p.description
        }
      });
    }
    return results;
  }

  public async saveAssignments(){
    if(this.assignerComponent){
      this.assignerComponent.loading.next(true);
    }
    const assigned = await this.projectService.setAssignments(this.project.id, this.assignments);
    this.assignmentForm.get('assignments').setValue(assigned, {
      emitEvent: false
    });
    console.log(assigned);
    if(this.assignerComponent){
      this.assignerComponent.loading.next(false);
    }
    this.touched = true;

    // Reupdate items

  }

  public async saveProject(){
    const industries = this.form.get('industry').value;
    const skills = this.form.get('skills').value;
    const developments = this.form.get('developments').value;
    const certifications = this.form.get('certifications').value;
    const client = this.form.get('for_Client_id').value;
    const newproject: Partial<Project> = {
      name: this.form.get('name').value,
      start_on: Globals.toISODate(this.form.get('dates').value.start),
      end_on: Globals.toISODate(this.form.get('dates').value.end),
      status: this.form.get('status').value,
      // due_on: Globals.toISODate(this.form.get('duedate').value),
      description: this.form.get('description').value,
      for_client: client ? client.id : null,
      industries: industries ? industries.map(o => o.id) : [],
      skills: skills ? skills.map((o: { id: number }) => o.id).filter(id => id != null) : [],
      developments: developments ? developments.map((o: { id: number }) => o.id).filter(id => id != null) : [],
      certifications: certifications ? certifications.map((o: { id: number }) => o.id).filter(id => id != null) : [],
      color: this.form.get('color').value,
      // members: this.assignments
      billable: this.form.get('billable').value,
    };

    console.log(newproject);

    const updatedproject = await this.projectService.saveProject(this.project.id, newproject, "Project Saved!", change => this.confirmClientChange(change));
    if (updatedproject) {
      this.project = updatedproject;
      this.touched = true;
      this.closeProject();
    }
  }

  // The dialog shows its subtitle as given, so it is translated here, one point per line.
  private async confirmClientChange(change: ClientChange): Promise<boolean>{
    const {open_drafts: drafts, uninvoiced_hours: hours, uninvoiced_trips: trips} = change;
    const key = (k: string, n?: number) => 'invoicing.client_change.' + k + (n === undefined ? '' : n === 1 ? '_one' : '_other');
    const say = (k: string, params = {}): Promise<string> => this.translate.get(k, params).toPromise();
    const count = (n: number) => ({count: n.toLocaleString(this.translate.currentLang)});
    const text = [
      drafts ? await say(key('drafts', drafts), count(drafts)) : null,
      hours || trips ? await say(key('work'), {hours: await say(key('hours', hours), count(hours)), trips: await say(key('trips', trips), count(trips))}) : null,
      await say(key('check')),
    ];
    return this.notifier.confirm(key('title'), text.filter(Boolean).join('\n\n'), key('confirm'), 'warning', 'cancel');
  }

  // An active Admin with invoicing on, on a client project of their own tenant (#1516), in view and edit mode.
  public get canSeeInvoicing(): boolean{
    const me = this.auth.user.getValue();
    return this.auth.isAdmin && me?.user_to_client?.status === UserStatus.ACTIVE && !!me?.invoicing_enabled && !!this.project?.id && !!this.project.for_client
      && this.project.Client_id === me.client?.id;
  }

  // Admin or the Invoicing grant with invoicing on, on a billable client project of their own tenant (#1518).
  public get canSeeInvoices(): boolean{
    const me = this.auth.user.getValue();
    return this.auth.canInvoice && !!me?.invoicing_enabled && !!this.project?.id && !!this.project.is_billable && !!this.project.for_client
      && this.project.Client_id === me.client?.id && !this.editing;
  }

  get focusField(): string | null {
    return this.embedded ? null : this.route.snapshot.queryParamMap.get('field');
  }

  public get canHoursOverview(): boolean{
    return this.auth.canInvoice && !!this.project?.id && !!this.project.is_billable && !!this.project.for_client && !this.editing;
  }

  openHoursOverview(){
    this.dialogService.open(HoursOverviewDialogComponent, {
      context: {clientId: this.project.for_client.id, projectId: this.project.id, projectName: this.project.name},
    });
  }

  public get canEdit(): boolean{
    return (this.auth.isAdmin || this.auth.isStaffer) && this.couldEdit;
  }

  public get range(): {start: Date, end: Date}{
    const dates = this.form?.get('dates')?.value;
    // The picker's control holds a Moment (see resetForm), but consumers of this
    // getter (e.g. user-project-assigner's preselectUser -> overlapsToday) still
    // expect a Date, so normalize here rather than at every call site.
    return dates ? {
      start: dates.start ? moment(dates.start).toDate() : null,
      end: dates.end ? moment(dates.end).toDate() : null
    } : {start: Globals.fromDateString(this.project.start_on), end: Globals.fromDateString(this.project.end_on)};
  }

  previewClient(for_client: Client) {
    if(this.canEdit) {
      this.dialogService.open<PopupDialogComponent<ClientComponent>>(PopupDialogComponent, {
        closeOnBackdropClick: true,
        context: {
          comp: ClientComponent,
          componentContext: {
            couldEdit: false,
            couldArchive: false,
            id: for_client.id
          }
        }
      });
    }
  }

  logo(client: Client) {
    return client && client.logo && client.logo.preview ? Globals.storage(client.logo.preview) : null;
  }

  get type(){
    return <TimelineType>this.settings.userSetting('timeline_type', TimelineType.Day);
  }
}
